/* eslint-disable camelcase */
import { useMemo } from 'react';
import { dayjs } from '@mdc/services';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { CSVLink } from 'react-csv';
import autoTable from 'jspdf-autotable';
const jsPDF = typeof window !== 'undefined' ? require('jspdf').jsPDF : null;

import MDCloudOpswatLogo from '../../../../mdc-styles/images/MD-Cloud-logo-black-blue.png';

const ALLOWED_TYPES = [
    'pdf',
    'csv'
];

const PDF_HEADER_VALUES = (t) => [[
    t('Tactic'),
    t('Technique Name'),
    t('Technique ID'),
    t('Subtechnique ID'),
    t('Permissions'),
    t('Source')
]];

const MitreExportButtons = ({ types, data, noOfRows, fileName }) => {
    const { t, ready } = useTranslation();

    const pdfData = useMemo(() => {
        const headRows = Object.keys(data);
        const bodyRows = headRows.map((value, index) => {
            return data[value].map((dataValue) => {
                if (dataValue?.techniques) {
                    const {
                        name: techniqueName,
                        technique_id: techniqueId,
                        subtechnique_id: subtechniqueId,
                        permissions_required: permissionsRequired,
                        source_uri: sourceURI
                    } = dataValue?.techniques[0];

                    return { id: headRows[index], techniqueName, techniqueId, subtechniqueId, permissionsRequired, sourceURI };
                }

                return '';
            });
        }).flat(1);

        return { headRows: headRows, bodyRows: bodyRows };
    }, [data]);

    const generatePDF = () => {
        const doc = new jsPDF({
            unit: 'px',
        });

        doc.setFont('helvetica', 'normal');

        const body = pdfData.headRows.map((header) => {
            const headRowEntriesLength = pdfData.bodyRows.filter((bodyRowTemp) => bodyRowTemp.id === header).length;
            const pdfBodyRows = pdfData.bodyRows.reduce((acc, item) => {
                if (item.id === header) {
                    // eslint-disable-next-line no-unused-vars
                    const { id, ...itemProcessed } = item;
                    acc.push(Object.values(itemProcessed));
                }

                return acc;
            }, []);

            if (pdfBodyRows.length === headRowEntriesLength) {
                const unshiftObject = {
                    rowSpan: headRowEntriesLength,
                    content: header,
                    styles: {
                        valign: 'middle',
                    }
                };

                if (headRowEntriesLength !== 0) {
                    pdfBodyRows[0].unshift(unshiftObject);
                } else {
                    // Remove this line if we should only display the headers for which we have data
                    pdfBodyRows.unshift([unshiftObject]);
                }
            }

            return pdfBodyRows;
        }).flat(1);

        autoTable(doc,
            {
                head: PDF_HEADER_VALUES(t),
                body: body,
                theme: 'grid',
                showHead: true,
                styles: {
                    fillColor: [255, 255, 255],
                    textColor: '#3D4A68',
                    lineWidth: 1,
                    lineColor: '#E9ECF2',
                    font: 'helvetica',
                    fontStyle: 'normal'
                },
                columnStyles: {
                    0: { minCellWidth: 70, halign: 'left', fontStyle: 'bold' },
                    1: { minCellWidth: 70 },
                    2: { minCellWidth: 50 },
                    3: { minCellWidth: 60 },
                    4: { minCellWidth: 60 },
                    5: { textColor: '#2672FB', fontStyle: 'bold' }
                },
                margin: { top: 130 },

                addPageContent: () => {
                    doc.addImage(MDCloudOpswatLogo, 'svg', 30, 20, 64, 27);
                    doc.setFontSize(10);
                    doc.setTextColor('#707DA1');
                    doc.text(`Generated ${dayjs().format('YYYY/MM/DD')} at ${dayjs().format('h:mm')} UTC`, doc.internal.pageSize.getWidth() - doc.getTextWidth(`Generated ${dayjs().format('YYYY/MM/DD')} at ${dayjs().format('h:mm')} UTC`) - 30, 25);
                    doc.setFontSize(13);
                    doc.setTextColor('#3D4A68');
                    doc.setFont('helvetica', 'bold');
                    doc.text('Mitre Att&ck Matrix Report', doc.internal.pageSize.getWidth() - doc.getTextWidth('Mitre Att&ck Matrix Report') - 30, 40);

                    doc.setDrawColor('#E9ECF2');
                    doc.line(30, 60, doc.internal.pageSize.getWidth() - 30, 61);

                    doc.setFontSize(11);
                    doc.setFont('helvetica', 'bold');
                    doc.setTextColor('#3D4A68');
                    doc.text('Report Source:', 30, 90);
                    doc.setFont('helvetica', 'normal');
                    doc.setFontSize(10);
                    doc.setTextColor('#2672FB');
                    doc.textWithLink(`${window?.location?.href?.replace('http://localhost:8801', 'https://metadefender.opswat.com')}`, 30, 104, { URL: `${window?.location?.href}` });

                    doc.setFontSize(9);
                    doc.setTextColor('#707DA1');
                    doc.text(`© ${dayjs().format('YYYY')} OPSWAT, Inc. All rights reserved. OPSWAT®, MetaDefender®, MetaAccess, Trust No File, Trust No Device,`, 30, doc.internal.pageSize.getHeight() - 40);
                    doc.text('and the OPSWAT logo are trademarks of OPSWAT, Inc', 30, doc.internal.pageSize.getHeight() - 30);

                }
            },
        );

        doc.save(`${fileName}.pdf`);
    };

    const csvData = useMemo(() => {
        const head = Object.keys(data);
        const body = Array(noOfRows).fill('').map((row, index) => {
            const mapping = Object.keys(data).map((value) => {
                if (data[value][index]) {
                    const {
                        subtechnique_id: subtechniqueId,
                        technique_id: techniqueId,
                        name: techniqueName,
                        permissions_required: permissionsRequired,
                        source_uri: sourceURI
                    } = data[value][index]?.techniques[0];

                    return `
Name: ${techniqueName}
Technique ID: ${techniqueId}
Subtechnique ID: ${subtechniqueId || '-'}
Permissions Required: ${permissionsRequired?.join(', ') || '-'}
Source URI: ${sourceURI}
`;
                }

                return '';
            });

            return [index, ...mapping];
        });

        return [['ID', ...head], ...body];
    }, [data]);

    const exportDataDom = useMemo(() => (type) => {
        switch (type) {
            case 'csv':
                return <CSVLink
                    data={csvData}
                    className='downloadSanitizedButton pl-0 btn btn-link'
                    filename={`${fileName}.csv`}
                    target='_blank'
                >
                    <i className='icon icon-down'/>
                    {t('Export')}{' '}{type.toUpperCase()}
                </CSVLink>;

            case 'pdf':
                return <button onClick={generatePDF} className='downloadSanitizedButton pl-0 btn btn-link'>
                    <i className='icon icon-down'/>
                    {t('Export')}{' '}{type.toUpperCase()}
                </button>;

            default:
                return <a href={'href'} download={`test.${type}`} className='downloadSanitizedButton pl-0 btn btn-link'>
                    <i className='icon icon-down'/>
                    {t('Export')}{' '}{type.toUpperCase()}
                </a>;
        }
    }, [csvData, fileName, t]);

    const buttonsDom = useMemo(() => {
        return types?.map((type) => {
            if (ALLOWED_TYPES.includes(type?.toLowerCase())) {
                return exportDataDom(type);
            }
        });
    }, [types]);

    if (!ready) {
        return;
    }

    return <div className='actions'>
        {buttonsDom}
    </div>;
};

MitreExportButtons.propTypes = {
    types: PropTypes.array.isRequired,
    data: PropTypes.object.isRequired,
    noOfRows: PropTypes.number,
    fileName: PropTypes.string.isRequired
};

export default MitreExportButtons;
